import { useCallback, useEffect, useRef, useState } from 'react';
import type { NotificationType } from '@/stores/notificationStore';

export type BulkRetryItem = { runId: string; triggerName: string };

export type ResubmitResult = { success: boolean; error?: string | undefined };

export type BulkRetrySummary = {
  succeeded: string[];
  failed: Array<{ runId: string; error?: string | undefined }>;
  /** Items not attempted because the workflow changed or the panel unmounted mid-batch. */
  aborted: string[];
};

type UseBulkRunRetryOptions = {
  /** Identity of the workflow the batch targets; a change mid-batch stops remaining submissions. */
  scopeKey: string | undefined;
  resubmit: (item: BulkRetryItem) => Promise<ResubmitResult>;
  onComplete?: (succeededRunIds: readonly string[]) => Promise<void>;
};

const UNMOUNTED = Symbol('unmounted');

/**
 * Sequentially resubmits runs, continuing past failed results. An unexpected
 * throw from `resubmit` stops the batch and rejects. Duplicate batches are
 * ignored while one is in flight.
 */
export const useBulkRunRetry = ({ scopeKey, resubmit, onComplete }: UseBulkRunRetryOptions) => {
  const [isPending, setIsPending] = useState(false);
  const inFlightRef = useRef(false);
  const scopeRef = useRef<string | undefined | typeof UNMOUNTED>(scopeKey);
  const cancellationRef = useRef(0);
  const cancel = useCallback(() => { cancellationRef.current += 1; }, []);

  useEffect(() => {
    scopeRef.current = scopeKey;
  }, [scopeKey]);

  useEffect(() => {
    return () => {
      scopeRef.current = UNMOUNTED;
    };
  }, []);

  const retry = useCallback(
    async (items: BulkRetryItem[]): Promise<BulkRetrySummary | null> => {
      if (inFlightRef.current || items.length === 0) return null;
      inFlightRef.current = true;
      setIsPending(true);
      const startScope = scopeRef.current;
      const startCancellation = cancellationRef.current;
      const summary: BulkRetrySummary = { succeeded: [], failed: [], aborted: [] };
      try {
        for (let index = 0; index < items.length; index++) {
          const item = items[index]!;
          if (scopeRef.current !== startScope || cancellationRef.current !== startCancellation) {
            summary.aborted.push(...items.slice(index).map((i) => i.runId));
            break;
          }
          // Expected failures arrive as `{ success: false }`; unexpected throws propagate.
          const result = await resubmit(item);
          if (result.success) {
            summary.succeeded.push(item.runId);
          } else {
            summary.failed.push({ runId: item.runId, error: result.error });
          }
        }
      } finally {
        try {
          if (summary.succeeded.length > 0) await onComplete?.(summary.succeeded);
        } finally {
          inFlightRef.current = false;
          if (scopeRef.current !== UNMOUNTED) setIsPending(false);
        }
      }
      return summary;
    },
    [resubmit, onComplete],
  );

  return { retry, isPending, cancel };
};

export type RunRetryController = ReturnType<typeof useBulkRunRetry>;

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

export const formatBulkRetryNotification = (
  summary: BulkRetrySummary,
  ineligibleCount: number,
): { type: NotificationType; title: string; message: string } => {
  const ok = summary.succeeded.length;
  const failed = summary.failed.length;
  const aborted = summary.aborted.length;
  const parts: string[] = [];
  if (ok > 0) parts.push(`${plural(ok, 'run')} resubmitted.`);
  if (failed > 0) {
    const firstError = summary.failed.find((f) => f.error)?.error;
    parts.push(`${plural(failed, 'run')} failed${firstError ? ` (${firstError})` : ''}.`);
  }
  if (aborted > 0) parts.push(`${plural(aborted, 'run')} not submitted because the workflow view changed.`);
  if (ineligibleCount > 0) parts.push(`${plural(ineligibleCount, 'selected run')} skipped as not eligible.`);

  let type: NotificationType = 'success';
  let title = ok === 1 ? 'Run resubmitted' : 'Runs resubmitted';
  if (ok === 0) {
    type = 'error';
    title = 'Retry failed';
  } else if (failed > 0 || aborted > 0 || ineligibleCount > 0) {
    type = 'warning';
    title = 'Retry partially completed';
  }
  return { type, title, message: parts.join(' ') };
};
